import "./order-messages-flyout.style.scss";

import { AvatarSize } from "@amzn/stencil-react-components/avatar";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor, ChipIconPosition } from "@amzn/stencil-react-components/chip";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import IconAlertTriangleFill from "@amzn/stencil-react-icons/icons/icon-alert-triangle-fill";
import { RoundedAvatar } from "@amzn/it-shop-shared-components";
import React, { FC, useState } from "react";

import { MessageItem } from "./message-item";
import { MessagesViewProps } from "./order-messages-flyout.types";
import { UserPermissions } from "@/services/user-permissions/user-permissions";

export const MessagesView: FC<MessagesViewProps> = ({
  title,
  messages,
  order,
  orderReference,
  submitComment,
  isLoading,
  isSubmitting,
  hasError,
}) => {
  const [message, setMessage] = useState("");
  const [showMessageField, setShowMessageField] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const username = UserPermissions.current.username;

  const isResponseRequired: boolean = messages?.comments?.[0]?.isResponseRequired ?? false;

  async function handleSendComment() {
    if (!message.trim()) return;
    setSubmitError(false);
    const success = await submitComment(order, message);
    if (success) {
      setMessage("");
    } else {
      setSubmitError(true);
    }
  }

  return (
    <>
      <Row>
        <Col width="100%" gridGap="S500" padding={{ bottom: "S500" }}>
          {(hasError || submitError) && (
            <Row>
              <MessageBanner
                dismissButtonAltText="Dismiss"
                type={MessageBannerType.Error}
                isDismissible={true}
              >
                Something went wrong. Please try again later.
              </MessageBanner>
            </Row>
          )}
          <Row>
            <Col width="100%" gridGap="S200">
              <Row>
                <Text fontSize="T300" fontWeight="bold" data-testid="messages-view-title">
                  {title}
                </Text>
              </Row>
              <Row>
                <Text color="gray" data-testid="messages-view-reference">
                  Order # {orderReference}
                </Text>
              </Row>
            </Col>
          </Row>
          {isResponseRequired && (
            <Row>
              <Chip
                icon={<IconAlertTriangleFill aria-hidden={true} />}
                backgroundColor={ChipBackgroundColor.Yellow}
                iconPosition={ChipIconPosition.Leading}
                data-testid="response-required-chip"
              >
                Your response is required
              </Chip>
            </Row>
          )}
          <Row justifyContent="flex-end">
            <Button
              size={ButtonSize.Small}
              variant={ButtonVariant.Tertiary}
              onClick={() => setShowMessageField(true)}
              data-testid="ask-question-button"
            >
              Ask additional question
            </Button>
          </Row>

          {(showMessageField || isResponseRequired) && (
            <Row>
              <Col width="100%" gridGap="S200">
                {username && (
                  <Row>
                    <RoundedAvatar
                      username={username}
                      size={AvatarSize.Small}
                      showUsername={false}
                      fullName="You"
                      renderNameWrapper={(name) => <Text fontWeight="medium">{name}</Text>}
                      showImage={true}
                    />
                  </Row>
                )}
                <Row>
                  <TextArea
                    data-testid="question-text-area"
                    width="100%"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </Row>
                <Row justifyContent="flex-end" padding="S200">
                  <Button
                    data-testid="submit-message-button"
                    variant={ButtonVariant.Primary}
                    onClick={handleSendComment}
                    disabled={!message.trim() || isSubmitting}
                  >
                    Submit
                    {isSubmitting ? <Spinner size={SpinnerSize.Small} /> : null}
                  </Button>
                </Row>
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      {isLoading && (
        <Row alignItems="center" justifyContent="center" padding="S200">
          <Text>Loading messages...</Text>
          <Spinner size={SpinnerSize.Small} />
        </Row>
      )}

      {messages?.comments?.map((msg, index) => {
        if (!msg?.posted?.on || !msg?.posted?.by || !msg?.comment) {
          return null;
        }

        return (
          <MessageItem
            timestamp={msg.posted.on}
            username={msg.posted.by}
            message={msg.comment}
            key={`message-${index}`}
            className={index === 0 ? "border-line-block" : "border-line-bottom"}
          />
        );
      })}
    </>
  );
};
